/**
 * Path codec for nested x-allOf rule authoring.
 *
 * Phase 1 restricted IF/THEN/ELSE selection to top-level fields. Phase 1b
 * surfaces nested fields via a FieldPath model:
 *
 *   property seg  -> `.name`
 *   arrayItem seg -> `[]` (stands for JSON Schema `items`, "every item")
 *
 * displayPath examples:
 *   address.country
 *   manifest[].consignee.email
 *   shipment.parcel.content.dimensions.units.uom
 *
 * The codec owns:
 *   - flattenFields(fields, {includeContainers}) -> AddressableField[]
 *   - pathToDisplay(path) / displayToPath(display)
 *   - emitPathCondition(path, leafSchema, {requireLeaf}) -> JSON Schema
 *   - parsePathFromProperties(branch) -> [{ path, leaf }]
 *   - OPERATOR_KEYWORDS for parser reuse
 *
 * Legacy flat schemas parse as length-1 property paths (their displayPath
 * equals the bare schemaKey) so no migration is needed.
 */

// JSON Schema keywords that mean "leaf operator" — when the parser sees any
// of these, it stops descending and classifies the node as a leaf.
export const OPERATOR_KEYWORDS = new Set([
	'const',
	'enum',
	'minimum',
	'maximum',
	'exclusiveMinimum',
	'exclusiveMaximum',
	'pattern',
	'format',
	'minLength',
	'maxLength',
	'contains',
	'not',
	'multipleOf',
	'uniqueItems',
]);

function isPlainObject(v) {
	return v !== null && typeof v === 'object' && !Array.isArray(v);
}

// ============ Path primitives ============

export function propertySeg(name) {
	return { kind: 'property', name };
}

export function arrayItemSeg() {
	return { kind: 'arrayItem' };
}

/** Render FieldPath to its display string. */
export function pathToDisplay(path) {
	if (!Array.isArray(path) || path.length === 0) return '';
	let out = '';
	for (const seg of path) {
		if (seg.kind === 'property') {
			out += out === '' ? seg.name : `.${seg.name}`;
		} else if (seg.kind === 'arrayItem') {
			out += '[]';
		}
	}
	return out;
}

/** Parse display string back into FieldPath. Inverse of pathToDisplay. */
export function displayToPath(display) {
	if (typeof display !== 'string' || display === '') return [];
	const path = [];
	// Tokenise: split on dots that aren't inside [], then strip []s into arrayItem segs.
	const parts = display.split('.');
	for (const part of parts) {
		// Each part may end with zero or more `[]`.
		const match = part.match(/^([^[\]]+)((?:\[\])*)$/);
		if (!match) {
			// Unknown shape — preserve as raw property name (escape hatch).
			path.push(propertySeg(part));
			continue;
		}
		const [, name, brackets] = match;
		path.push(propertySeg(name));
		const arrayCount = brackets.length / 2;
		for (let i = 0; i < arrayCount; i += 1) path.push(arrayItemSeg());
	}
	return path;
}

// ============ Flatten ============

/**
 * Walk a local-fields tree depth-first, emitting one AddressableField per
 * node that can be referenced in a rule. Container fields precede their
 * children so the selector can render a grouped list without re-sorting.
 *
 * Output shape:
 *   {
 *     path: FieldPath,
 *     displayPath: string,
 *     dataType: string,
 *     arrayItemType?: string,
 *     format?: string,
 *     parentFieldId?: string,
 *     topLevel: string   // first property segment's name — for grouping
 *   }
 */
export function flattenFields(fields, { includeContainers = true } = {}) {
	const out = [];

	function walk(node, path) {
		if (!node || node.status === 'Deleted') return;

		const dataType = node.dataType || 'string';
		const isContainer = dataType === 'object'
			|| (dataType === 'array' && node.arrayItemType === 'object');

		const entry = {
			path,
			displayPath: pathToDisplay(path),
			dataType,
			topLevel: path[0]?.kind === 'property' ? path[0].name : '',
			parentFieldId: node.fieldId,
		};
		if (node.arrayItemType) entry.arrayItemType = node.arrayItemType;
		if (node.format) entry.format = node.format;

		if (!isContainer || includeContainers) out.push(entry);

		const children = Array.isArray(node.children) ? node.children : [];
		if (dataType === 'object') {
			for (const child of children) {
				walk(child, [...path, propertySeg(child.dataElementName)]);
			}
		} else if (dataType === 'array' && node.arrayItemType === 'object') {
			// array<object> — enter the item via an arrayItem segment, then walk
			// each child property as sub-paths of the item.
			for (const child of children) {
				walk(child, [...path, arrayItemSeg(), propertySeg(child.dataElementName)]);
			}
		}
	}

	for (const field of Array.isArray(fields) ? fields : []) {
		walk(field, [propertySeg(field.dataElementName)]);
	}
	return out;
}

// ============ Emit ============

/**
 * Wrap a leaf schema fragment with property/items wrappers derived from the
 * path, producing a nested JSON Schema suitable for embedding in an IF body
 * or THEN/ELSE branch.
 *
 *   emitPathCondition([prop('address'), prop('country')], { const: 'SG' })
 *     = {
 *         properties: {
 *           address: {
 *             properties: { country: { const: 'SG' } },
 *             required: ['country']
 *           }
 *         },
 *         required: ['address']
 *       }
 *
 * `requireLeaf` controls whether the innermost property segment is added to
 * its parent's `required` list (the `required-at-leaf` toggle from the UI).
 *
 * `requiredContainerDisplayPaths` (Set<string>) controls the same for
 * intermediate container segments. When `undefined`, containers are always
 * required — legacy behaviour preserved for UI-authored rules. When a Set
 * is provided (for uploaded rules parsed via parseBranch/parseIfBody),
 * only containers whose display-path is in the set are marked required,
 * so round-trips no longer inject arrays like `required: ["order"]` that
 * the uploaded schema never had.
 *
 * arrayItem segments never contribute to `required`.
 */
export function emitPathCondition(
	path,
	leafSchema,
	{ requireLeaf = true, requiredContainerDisplayPaths } = {},
) {
	if (!Array.isArray(path) || path.length === 0) return leafSchema || {};
	let acc = leafSchema || {};
	const honorContainerSet = requiredContainerDisplayPaths instanceof Set;

	for (let i = path.length - 1; i >= 0; i -= 1) {
		const seg = path[i];
		if (seg.kind === 'property') {
			const isLeaf = i === path.length - 1;
			let shouldRequire;
			if (isLeaf) {
				shouldRequire = requireLeaf;
			} else if (honorContainerSet) {
				shouldRequire = requiredContainerDisplayPaths.has(pathToDisplay(path.slice(0, i + 1)));
			} else {
				shouldRequire = true;
			}
			const wrapped = { properties: { [seg.name]: acc } };
			if (shouldRequire) wrapped.required = [seg.name];
			acc = wrapped;
		} else if (seg.kind === 'arrayItem') {
			acc = { items: acc };
		}
	}
	return acc;
}

/**
 * Deep-merge two emitted path-wrapper fragments so rules that share a parent
 * path don't trample each other's siblings.
 *
 *   merge emitted('address.country', ...) with emitted('address.postalCode', ...)
 *   -> { properties: { address: { properties: { country:..., postalCode:... },
 *                                  required: ['country','postalCode'] } },
 *        required: ['address'] }
 *
 * Leaf operator objects (no `properties` / `items`) on collision fall back to
 * deepMerge semantics: rightmost wins per-key. In practice two rules should
 * not collide at the same leaf, but we stay well-defined.
 */
export function mergePathFragments(a, b) {
	if (a === undefined || a === null) return b;
	if (b === undefined || b === null) return a;
	if (!isPlainObject(a) || !isPlainObject(b)) return b;

	const out = { ...a };
	for (const [key, bv] of Object.entries(b)) {
		const av = out[key];
		if (key === 'required' && Array.isArray(av) && Array.isArray(bv)) {
			const seen = new Set(av);
			const merged = [...av];
			for (const name of bv) if (!seen.has(name)) { merged.push(name); seen.add(name); }
			out[key] = merged;
			continue;
		}
		if (isPlainObject(av) && isPlainObject(bv)) {
			out[key] = mergePathFragments(av, bv);
			continue;
		}
		out[key] = bv;
	}
	return out;
}

// ============ Parse ============

function hasOperatorKeyword(schema) {
	if (!isPlainObject(schema)) return false;
	for (const key of Object.keys(schema)) {
		if (OPERATOR_KEYWORDS.has(key)) return true;
	}
	// `type: 'null'` is an operator-ish leaf from Phase 1's `type_is_null`.
	if (schema.type === 'null') return true;
	if (Array.isArray(schema.type) && schema.type.includes('null') && schema.type.length === 1) {
		return true;
	}
	return false;
}

/**
 * Walk a `{ properties, required, items, ... }` shape and yield one entry
 * per terminal leaf, reconstructing the path from the root.
 *
 * A node is terminal when it carries any operator keyword, OR when it is a
 * primitive-typed (non-object, non-array-of-object) leaf.
 *
 * Shapes that can't be classified (e.g. items: { oneOf: [...] }) fall
 * through to a length-1 raw entry at the current path — caller decides
 * whether to treat them as legacy flat or to hand off as `raw`.
 */
export function parsePathFromProperties(root) {
	const out = [];

	function walk(node, path) {
		if (!isPlainObject(node)) {
			out.push({ path, leaf: node });
			return;
		}

		const ops = hasOperatorKeyword(node);
		const hasProps = isPlainObject(node.properties);
		const hasItems = isPlainObject(node.items) && isPlainObject(node.items.properties);

		// Leaves: anything with an operator keyword at this level, OR nodes
		// that carry no further structure. We don't recurse into `properties`
		// if the node itself declares a leaf operator — that preserves Phase 1
		// semantics where a top-level field can carry both a type and a const.
		if (ops || (!hasProps && !hasItems)) {
			out.push({ path, leaf: node });
			return;
		}

		if (hasProps) {
			const required = new Set(Array.isArray(node.required) ? node.required : []);
			for (const [name, child] of Object.entries(node.properties)) {
				const childPath = [...path, propertySeg(name)];
				walk(child, childPath);
				// Required metadata rides on the leaf entry via parseIfBody/parseBranch.
				// We don't annotate it here; callers fetch from node.required.
			}
			// Unused but kept available for potential future use.
			// eslint-disable-next-line no-unused-expressions
			required;
			return;
		}

		if (hasItems) {
			walk(node.items, [...path, arrayItemSeg()]);
			return;
		}
	}

	if (!isPlainObject(root)) return out;
	if (!isPlainObject(root.properties)) {
		// Nothing to walk; leave empty.
		return out;
	}
	for (const [name, child] of Object.entries(root.properties)) {
		walk(child, [propertySeg(name)]);
	}
	return out;
}

/**
 * Walk a branch schema and collect `required` metadata at every level so the
 * caller can mark leaf constraints required when the innermost property
 * segment appears in its parent's `required` list.
 *
 *   { path, required }
 */
export function collectRequiredByPath(root) {
	const out = new Map();

	function walk(node, path) {
		if (!isPlainObject(node)) return;
		if (isPlainObject(node.properties)) {
			const reqSet = new Set(Array.isArray(node.required) ? node.required : []);
			for (const [name, child] of Object.entries(node.properties)) {
				const childPath = [...path, propertySeg(name)];
				out.set(pathToDisplay(childPath), reqSet.has(name));
				walk(child, childPath);
			}
		}
		if (isPlainObject(node.items)) {
			walk(node.items, [...path, arrayItemSeg()]);
		}
	}

	walk(root, []);
	return out;
}
